import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { getItemsPerCategory, UpdateProduct } from "../../../server/Product.js";
import {
  CategoryUpdate,
  CategoryAdd,
  CategoryDelete,
  getAllCategory,
  getCategoriesBySlug,
  toggleExpandCardAction,
  resetExpandedCards,
} from "../../../server/Category";
import {
  FiEdit2,
  FiCheckSquare,
  FiSquare,
  FiSave,
  FiPlus,
  FiTrash,
} from "react-icons/fi";
import "./showCategory.scss";
import AddCategoryDialog from "./addCategoryDialog";
import { PlusCircle, Upload } from "lucide-react";
import DeleteCategoryDialog from "./deleteCategoryDialog";
import { sortCategoryId } from "../../../../axiosInstance.js";
import MoveCategoryDialog from "./MoveCategoryDialog.js";
import ReactTooltip from "react-tooltip";

const ShowCategory = ({
  parentId = "ROOT",
  isNested = false,
  level = 0,
  categoriesState: externalCategories = null,
}) => {
  const getId = (item) => item?.Id ?? item?.id;
  const getParentId = (item) => item?.parentId ?? item?.FatherId ?? "ROOT";
  const getName = (item) => item?.name ?? item?.Name ?? "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const allCategoriesState = useSelector((state) => state.category.categories);
  const stationId = useSelector((state) => state.user.stationId);
  const expandedCards = useSelector((state) => state.category.expandedCards);

  const [currentItems, setCurrentItems] = useState([]);
  const [addingDialogOpen, setAddingDialogOpen] = useState(false);
  const [fatherIdAddCategory, setFatherIdAddCategory] = useState(null);
  const [fatherSlugAddCategory, setFatherSlugAddCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [moveCategoryOpen, setMoveCategoryOpen] = useState(null);
  const [renamingCardId, setRenamingCardId] = useState(null);
  const [renameInputValue, setRenameInputValue] = useState("");

  useEffect(() => {
    let filteredItems;
    const parentIdAsString = String(parentId);

    if (externalCategories) {
      filteredItems = Array.isArray(externalCategories)
        ? externalCategories
        : [];
    } else {
      filteredItems = allCategoriesState.filter(
        (item) => String(getParentId(item)) === parentIdAsString,
      );
    }
    filteredItems.sort((a, b) => (a.OrderNumber || 0) - (b.OrderNumber || 0));
    setCurrentItems(filteredItems);
  }, [allCategoriesState, externalCategories, parentId]);

  useEffect(() => {
    if (stationId) {
      dispatch(getAllCategory(stationId));
    }
  }, [stationId, dispatch]);

  useEffect(() => {
    if (level === 0 && navigationType === "PUSH") {
      dispatch(resetExpandedCards());
    }
  }, [location.pathname, navigationType, dispatch, level]);

  const toggleExpandCard = (cardId) => {
    dispatch(toggleExpandCardAction(cardId));
  };

  const updateCategory = ({ id, title, value, slug }) => {
    dispatch(
      CategoryUpdate(
        {
          Mkt: id,
          Title: title,
          value: value,
          slug: slug,
        },
        stationId,
      ),
    );
  };

  const addNewCategory = async (newCategoryName, isFamily) => {
    if (newCategoryName.trim() === "") return;
    const data = {
      parentId: fatherIdAddCategory?.toString() || "ROOT",
      name: newCategoryName,
      branch: stationId,
      isFamily: isFamily,
      status: "ACTIVE",
      parentSlug: fatherSlugAddCategory || "_root",
    };
    const createSlug = (slug) => {
      return slug
        .normalize("NFKC")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    };

    try {
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(newCategoryName)}&langpair=he|en`,
      );
      const result = await response.json();
      const translatedText =
        result.responseData?.translatedText || newCategoryName;
      const tempSlug = createSlug(translatedText);
      dispatch(CategoryAdd(tempSlug, data, stationId));
    } catch (error) {
      console.error("Error translating category name:", error);
      const slug = createSlug(newCategoryName);
      dispatch(CategoryAdd(slug, data, stationId));
    }

    setAddingDialogOpen(false);
  };

  const [updatingItems, setUpdatingItems] = useState({});

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const originalItems = [...currentItems];
    const reorderedItems = [...currentItems];
    const [movedItem] = reorderedItems.splice(source.index, 1);
    reorderedItems.splice(destination.index, 0, movedItem);
    setCurrentItems(reorderedItems);
    if (updatingItems[movedItem.id]) return;
    setUpdatingItems((prev) => ({ ...prev, [movedItem.id]: true }));
    const positionPayload = (() => {
      const total = reorderedItems.length;
      const idx = destination.index;
      if (idx === 0) return { kind: "prepend" };
      if (idx === total - 1) return { kind: "append" };
      const beforeItem = reorderedItems[idx - 1];
      const afterItem = reorderedItems[idx + 1];
      return {
        kind: "betweenByRank",
        before: beforeItem.rank ?? beforeItem.id,
        after: afterItem.rank ?? afterItem.id,
      };
    })();

    try {
      await dispatch(
        CategoryUpdate(
          {
            slug: movedItem.slug,
            Title: "position",
            value: positionPayload,
            Mkt: movedItem.id,
            rank: movedItem.rank,
          },
          stationId,
        ),
      ).unwrap();
    } catch (error) {
      console.error("Failed to update category position:", error);
      setCurrentItems(originalItems);
    } finally {
      setUpdatingItems((prev) => ({ ...prev, [movedItem.id]: false }));
    }
  };

  const handleCardClick = (id, childrenCount, slug) => {
    if (childrenCount === 0) {
      navigate(`/products-edit/${id}/${slug}`);
    } else {
      getCategoriesBySlug(stationId, slug);
    }
    toggleExpandCard(id);
  };

  const toggleActiveStatus = (card) => {
    const newStatusValue = card.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updateCategory({
      id: getId(card),
      title: "status",
      value: newStatusValue,
      slug: card.slug,
    });

    if (card.status === "ACTIVE") {
      getItemsPerCategory(getId(card), stationId)
        .then((res) => {
          res.data.forEach((prod) =>
            UpdateProduct({
              Mkt: prod.Mkt,
              StationId: stationId,
              Title: "DisableProductId",
              value: "5",
            }),
          );
        })
        .catch((e) => console.error("Error disabling products:", e));
    }
  };

  const startRenamingCard = (card) => {
    setRenamingCardId(getId(card));
    setRenameInputValue(getName(card));
  };

  const saveRenamingCard = (card) => {
    updateCategory({
      id: getId(card),
      title: "name",
      value: renameInputValue,
      slug: card.slug,
    });
    setRenamingCardId(null);
  };

  const openAddingDialog = (id, slug) => {
    setFatherIdAddCategory(id);
    setFatherSlugAddCategory(slug);
    setAddingDialogOpen(true);
  };
  useEffect(() => {}, [fatherIdAddCategory]);
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const deleteEmptyCategory = (categorySlug) => {
    dispatch(CategoryDelete(categorySlug, stationId));
  };

  const handleDelete = (item) => {
    console.log("item", item);
    getItemsPerCategory(item.slug, stationId)
      .then((x) => {
        if (x.data.count === 0) {
          deleteEmptyCategory(item.slug);
        } else {
          setCategoryToDelete(item.slug);
          setDeleteDialogOpen(true);
        }
      })
      .catch((e) => console.error("Error checking products before delete:", e));
  };

  const deleteCtegoryWithProducts = () => {
    setDeleteDialogOpen(false);
    getItemsPerCategory(categoryToDelete, stationId)
      .then((x) => {
        const products = x.data;
        products.forEach((prod) => {
          UpdateProduct({
            Mkt: prod.Mkt,
            Title: "categoryId",
            value: sortCategoryId,
            StationId: stationId,
          });
        });
        deleteEmptyCategory(categoryToDelete);
      })
      .catch((e) => console.error("Error moving products before delete:", e));
  };

  const handleMoving = (categoryId) => {
    setMoveCategoryOpen(() => categoryId);
  };

  const totalCounts = currentItems.reduce(
    (acc, item) => ({
      admin: acc.admin + (item.AdminProductCount || 0),
      customer: acc.customer + (item.CustomerProductCount || 0),
      active: acc.active + (item.ActiveProductCount || 0),
    }),
    { admin: 0, customer: 0, active: 0 },
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={isNested ? `nested-list-${level}` : "list"}>
        {(provided) => (
          <div
            className={`categories-container ${isNested ? "nested-categories" : ""}`}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {level === 0 && (
              <div className="category-summary">
                <span className="summary-title">סה"כ מוצרים:</span>
                <div className="summary-values">
                  <span>כל המוצרים: {totalCounts.admin}</span>
                  <span>פעילים: {totalCounts.active}</span>
                  <span>לקוח: {totalCounts.customer}</span>
                </div>
              </div>
            )}
            {level === 0 && (
              <div
                className="category-item add-category-item"
                onClick={() => openAddingDialog(0, 0)}
              >
                <div className="add-category-content">
                  <div className="circle-icon">
                    <PlusCircle className="plus-icon" />
                  </div>
                  <p>הוסף מחלקה חדשה</p>
                </div>
              </div>
            )}
            {currentItems.map((item, index) => {
              const children = Array.isArray(item.children)
                ? item.children
                : [];
              return (
                <Draggable
                  key={getId(item)?.toString()}
                  draggableId={getId(item)?.toString()}
                  index={index}
                  isDragDisabled={
                    children.length > 0 && expandedCards[getId(item)]
                  }
                >
                  {(provided) => (
                    <div
                      className="category-item"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <div
                        className={`category-content level-${item.isFamily ? "family" : level}`}
                      >
                        {renamingCardId === getId(item) ? (
                          <form
                            className="rename-form"
                            onSubmit={(e) => {
                              e.preventDefault();
                              saveRenamingCard(item);
                            }}
                          >
                            <input
                              type="text"
                              value={renameInputValue}
                              onChange={(e) =>
                                setRenameInputValue(e.target.value)
                              }
                              className="rename-input"
                              autoFocus
                              aria-label="Rename category"
                            />
                            <button
                              type="submit"
                              className="icon-button"
                              aria-label="Save new name"
                            >
                              <FiSave size={20} />
                            </button>
                          </form>
                        ) : (
                          <span
                            className="category-name"
                            onClick={() =>
                              handleCardClick(
                                getId(item),
                                children.length,
                                item.slug,
                              )
                            }
                          >
                            {getName(item)}
                            <span className="category-counts">
                              <span>
                                כל המוצרים : {item.AdminProductCount ?? 0}
                              </span>
                              |
                              <span>
                                פעילים: {item.ActiveProductCount ?? 0}
                              </span>
                              |
                              <span>
                                לקוח: {item.CustomerProductCount ?? 0}
                              </span>
                            </span>
                          </span>
                        )}
                        {item.Id !== sortCategoryId && (
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {children.length === 0 && (
                              <button
                                onClick={() =>
                                  handleDelete({
                                    id: getId(item),
                                    slug: item.slug,
                                  })
                                }
                                className="delete-button"
                              >
                                <FiTrash size={20} />
                              </button>
                            )}
                            <button
                              onClick={() => startRenamingCard(item)}
                              className="icon-button"
                            >
                              <FiEdit2 size={20} />
                            </button>
                            <button
                              data-tip="העברה לקטגורית אב אחרת"
                              data-for="movingCategory"
                              onClick={() => handleMoving(getId(item))}
                              className="icon-button"
                            >
                              <Upload size={20} />
                            </button>
                            <button
                              onClick={() => toggleActiveStatus(item)}
                              className="toggle-active-button"
                            >
                              {item.status === "ACTIVE" ? (
                                <FiCheckSquare size={20} />
                              ) : (
                                <FiSquare size={20} />
                              )}
                            </button>
                            {!item.isFamily && level < 3 && (
                              <button
                                onClick={() =>
                                  openAddingDialog(getId(item), item.slug)
                                }
                                className="add-category-button"
                              >
                                <FiPlus size={20} />
                              </button>
                            )}
                          </span>
                        )}
                      </div>

                      {expandedCards[getId(item)] && children.length > 0 && (
                        <ShowCategory
                          parentId={getId(item)}
                          isNested={true}
                          level={level + 1}
                          categoriesState={children}
                          className="nested-categories"
                        />
                      )}
                    </div>
                  )}
                </Draggable>
              );
            })}
            <ReactTooltip id="movingCategory" place="top" effect="solid" />
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {addingDialogOpen && (
        <AddCategoryDialog
          handleAdding={addNewCategory}
          handleClose={setAddingDialogOpen}
          categoryLevel={fatherIdAddCategory === 0 ? 0 : level + 1}
        />
      )}
      {deleteDialogOpen && (
        <DeleteCategoryDialog
          handleDelete={deleteCtegoryWithProducts}
          handleClose={closeDeleteDialog}
        />
      )}
      {moveCategoryOpen && (
        <MoveCategoryDialog
          idToMove={moveCategoryOpen}
          updateCategory={updateCategory}
          handleClose={setMoveCategoryOpen}
        />
      )}
    </DragDropContext>
  );
};

export default ShowCategory;
